import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from "react-helmet";

import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import { FAQ_CATEGORIES, FAQ_INTRO } from './faqData';

import '../AboutUsPage/aboutV2.css';
import '../legalV2.css';

// FAQPage structured data, generated from the same list the page renders.
const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_CATEGORIES.flatMap((category) =>
        category.items.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
        }))
    ),
};

const Faq = ({ path }) => {
    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    // One open question per category: { [categoryIndex]: itemIndex }
    const [openItems, setOpenItems] = useState({});

    // Metadata and schema data.
    const [metadata, setMetadata] = useState({title: "JJFIndia | "});
    const [schemadata, setSchemadata] = useState([faqPageSchema]);

    // Getting the schemadata and setting the schema data.
    useEffect(() => {
        let tempMetadata = metadataJSON[path];
        let tempSchemadata = schemadataJSON[path];

        if(tempMetadata){
        setMetadata(tempMetadata);
        }else {
        tempMetadata = {
            title: "JJFIndia | Home",
            description: "JJFIndia home page",
            keywords: "",
            robots: "index, follow",
            canonical: "https://jjfindia.com/",
            openGraph: {
                title: "JJFIndia | Home",
                description: "JJFIndia home page",
                url: "https://jjfindia.com/"
            },
            twitter: {
                card: "summary_large_image",
                title: "JJFIndia | Home",
                description: "JJFIndia home page"
            }
        }

        setMetadata(tempMetadata);
        }

        // setting schema data (breadcrumb from the JSON file + generated FAQPage)
        if(tempSchemadata) {
        const base = Array.isArray(tempSchemadata) ? tempSchemadata : [tempSchemadata];
        setSchemadata([...base.filter((s) => s["@type"] !== "FAQPage"), faqPageSchema]);
        }

    }, [path]);

    const toggleItem = (categoryIndex, itemIndex) => {
        setOpenItems((prev) => ({
            ...prev,
            [categoryIndex]: prev[categoryIndex] === itemIndex ? null : itemIndex,
        }));
    };


    return (
        <>
            <Helmet>
                {/* Basic Meta Tags */}
                <title>{metadata.title}</title>
                <meta name="description" content={metadata.description} />
                <meta name="keywords" content={metadata.keywords} />
                <meta name="robots" content={metadata.robots} />

                {/* Canonical URL */}
                <link rel="canonical" href={metadata.canonical} />

                {/* Open Graph */}
                <meta property="og:title" content={metadata.openGraph?.title} />
                <meta property="og:description" content={metadata.openGraph?.description} />
                <meta property="og:url" content={metadata.canonical} />

                {/* Twitter Cards */}
                {/* <meta name="twitter:card" content={metadata.twitter?.card} /> */}
                <meta name="twitter:title" content={metadata.twitter?.title} />
                <meta name="twitter:description" content={metadata.twitter?.description} />

                {/* Setting up schema data */}
                <script  type="application/ld+json" >{JSON.stringify(schemadata, null, 2)}</script>

                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
            </Helmet>
            <div className="jjfv2 about-live" ref={rootRef}>
                <HeaderV2 />
                <main className="page_content">
                    <AboutHero
                        eyebrow="Help centre"
                        title="FAQs"
                        crumbs={[{ label: "Home", to: "/" }, { label: "FAQs" }]}
                    />

                    <section className="about pt-60">
                        <div className="container">
                            <div className="w-100 row align-items-center justify-content-center">
                                <div className="col-lg-10">
                                    <div className="aboutContent aboutContent--style2">
                                        <div className="sectionTitle d-flex flex-column justify-content-center text-center">
                                            <span className="sectionTitle__small w-100 justify-content-center" style={{ textTransform: "none" }}>
                                                <i className="fa-solid fa-heart btn__icon"></i>
                                                FAQs
                                            </span>
                                            <h2 className="sectionTitle__big text-center" style={{textTransform:'none'}}>GENERAL FAQs FOR JAINAM JIVIKA FOUNDATION</h2>
                                        </div>
                                        <p className="aboutContent__text text-center" style={{ fontSize: '20px' }}>
                                            {FAQ_INTRO}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {FAQ_CATEGORIES.map((category, ci) => {
                        const isLast = ci === FAQ_CATEGORIES.length - 1;
                        return (
                            <section className="" key={category.title}>
                                <div className="container">
                                    <div className="row">
                                        <div className="col-lg-12 col-md-12">
                                            <div className="wpo-service-single-wrap">
                                                <div className="wpo-benefits-section">
                                                    <div className='sectionTitle'>
                                                        <h2 className="text-center sectionTitle__big" style={{ textTransform: 'capitalize' }}>{category.title}</h2>
                                                    </div>
                                                    <div className={`row${isLast ? " pb-40" : ""}`}>
                                                        <div className="col-lg-12 col-12">
                                                            <div className="wpo-benefits-item">
                                                                <div className="accordion" id={`faqAccordion${ci}`}>
                                                                    {category.items.map((item, qi) => {
                                                                        const isOpen = openItems[ci] === qi;
                                                                        const id = `faq-${ci}-${qi}`;
                                                                        return (
                                                                            <div className="accordion-item" key={item.q}>
                                                                                <h3 className="accordion-header" id={`heading-${id}`}>
                                                                                    <button
                                                                                        className={`accordion-button ${isOpen ? "" : "collapsed"}`}
                                                                                        type="button"
                                                                                        style={{ fontSize: '20px' }}
                                                                                        onClick={() => toggleItem(ci, qi)}
                                                                                        aria-expanded={isOpen ? "true" : "false"}
                                                                                        aria-controls={`collapse-${id}`}
                                                                                    >
                                                                                        {item.q}
                                                                                    </button>
                                                                                </h3>
                                                                                <div
                                                                                    id={`collapse-${id}`}
                                                                                    className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}
                                                                                    aria-labelledby={`heading-${id}`}
                                                                                    data-bs-parent={`#faqAccordion${ci}`}
                                                                                >
                                                                                    <div className="accordion-body" style={{ fontSize: '18px' }}>
                                                                                        <p>{item.a}</p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        );
                    })}

                </main>

                <FooterV2 />
            </div>
        </>
    )
};

export default Faq;
